import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { LayoutDashboard, Settings, Sun, Moon, LogOut, User, Eye, FileSignature, Smartphone, LifeBuoy, Menu, X, CalendarRange } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { clientById } from "@/data/mockData";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddToDeviceDialog } from "@/components/common/AddToDeviceDialog";
import { BookmarkAppPrompt } from "@/components/portal/BookmarkAppPrompt";
import { fetchPortalSummary } from "@/lib/portalData";
import { getRegistryClient, clientDisplayName as registryName } from "@/lib/clientRegistry";

export function ClientPortalLayout() {
  const { theme, toggleTheme } = useTheme();
  const { user, isClient, logout } = useAuth();
  const navigate = useNavigate();
  const { clientId: paramId } = useParams();
  const [addOpen, setAddOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [realClientName, setRealClientName] = useState(null);

  const base = paramId ? `/portal/${paramId}` : "/portal";
  const previewMode = !isClient && !!paramId;
  const mockClient = clientById(previewMode ? paramId : user?.clientId);

  // Recupera il nome reale del cliente dal backend (mockData contiene solo i client legacy c_*).
  useEffect(() => {
    let cancelled = false;
    fetchPortalSummary(previewMode ? paramId : undefined)
      .then((s) => { if (!cancelled) setRealClientName(s?.profile?.name || null); })
      .catch(() => { if (!cancelled) setRealClientName(null); });
    return () => { cancelled = true; };
  }, [paramId, previewMode]);

  const clientDisplayName = realClientName
    || (previewMode ? registryName(getRegistryClient(paramId)) : "")
    || mockClient?.name || (isClient ? user?.name : "") || "Cliente";
  const client = mockClient || (clientDisplayName ? { name: clientDisplayName } : null);

  // Contatti secondari: stesso portale del principale ma SENZA la sezione Amministrazione.
  const isSecondary = isClient && (user?.contact_role || "primary") === "secondary";

  const NAV = [
    { to: base, label: "Dashboard", icon: LayoutDashboard, end: true, testid: "portal-nav-dashboard" },
    { to: `${base}/planning`, label: "Planning", icon: CalendarRange, testid: "portal-nav-planning" },
    { to: `${base}/assistenza`, label: "Assistenza", icon: LifeBuoy, testid: "portal-nav-support" },
    ...(isSecondary ? [] : [{ to: `${base}/amministrazione`, label: "Amministrazione", icon: FileSignature, testid: "portal-nav-admin" }]),
    { to: `${base}/impostazioni`, label: "Impostazioni account", icon: Settings, testid: "portal-nav-settings" },
  ];

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="sticky top-0 z-30 hidden h-screen w-60 shrink-0 flex-col border-r border-border/60 bg-card lg:flex">
        <div className="flex h-16 items-center gap-2.5 px-6">
          <img src="/logo-ae.png" alt="Aesthetic OS" className="h-7 w-auto object-contain" data-testid="portal-brand-logo" />
          <div className="leading-tight">
            <div className="font-display text-sm font-semibold tracking-tight text-foreground">
              Aesthetic <span className="text-muted-foreground">OS</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">&gt; portale cliente</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                  isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-4">
          <div className="rounded-xl border border-border/60 bg-muted/40 p-3">
            <p className="text-xs font-medium text-foreground">{client?.name}</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              Gestisci i tuoi lead in un unico posto.
            </p>
          </div>
          <Button variant="outline" size="sm" className="mt-3 w-full justify-start gap-2 text-xs"
                  onClick={() => setAddOpen(true)} data-testid="portal-add-to-device">
            <Smartphone className="h-3.5 w-3.5" /> Aggiungi accesso rapido
          </Button>
        </div>
      </aside>

      {/* Drawer mobile: overlay + pannello con la navigazione del portale */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="portal-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-72 max-w-[82vw] flex-col border-r border-border/60 bg-card shadow-xl">
            <div className="flex h-16 items-center justify-between px-4">
              <div className="flex items-center gap-2.5">
                <img src="/logo-ae.png" alt="Aesthetic OS" className="h-7 w-auto object-contain" />
                <div className="leading-tight">
                  <div className="font-display text-sm font-semibold tracking-tight text-foreground">Aesthetic <span className="text-muted-foreground">OS</span></div>
                  {clientDisplayName && <div className="max-w-[46vw] truncate text-[10px] text-muted-foreground">{clientDisplayName}</div>}
                </div>
              </div>
              <button onClick={() => setMobileNavOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Chiudi menu" data-testid="portal-mobile-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
              {NAV.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} data-testid={`${item.testid}-mobile`}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) => cn(
                    "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl sm:px-6">
          {/* Mobile: hamburger + logo Aesthetic OS + ragione sociale del cliente */}
          <div className="flex items-center gap-2.5 lg:hidden">
            <button onClick={() => setMobileNavOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-accent" aria-label="Apri menu" data-testid="portal-mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
            <img src="/logo-ae.png" alt="Aesthetic OS" className="h-6 w-auto object-contain" data-testid="portal-mobile-logo" />
            <div className="leading-tight">
              <div className="font-display text-xs font-semibold tracking-tight text-foreground">Aesthetic <span className="text-muted-foreground">OS</span></div>
              {clientDisplayName && <div className="max-w-[42vw] truncate text-[10px] text-muted-foreground" data-testid="portal-mobile-client-name">{clientDisplayName}</div>}
            </div>
          </div>
          {previewMode && (
            <div className="flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-xs font-medium text-sky-500" data-testid="portal-preview-banner">
              <Eye className="h-3.5 w-3.5" /> Anteprima interna · {client?.name}
              <button onClick={() => navigate(`/clienti/${paramId}`)} className="ml-1 underline hover:no-underline">Torna al workspace</button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <Button data-testid="portal-theme-toggle" variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9 text-muted-foreground hover:text-foreground">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="portal-user-menu" className="ml-1 flex items-center gap-2 rounded-full outline-none">
                  <Avatar className="h-8 w-8 border border-border/60">
                    <AvatarFallback className="text-xs">{user?.initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">{user?.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user?.email || "Da configurare"}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="portal-menu-settings" onClick={() => navigate(`${base}/impostazioni`)}>
                  <User className="mr-2 h-4 w-4" /> Impostazioni account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="portal-menu-logout" onClick={() => { logout(); navigate("/login"); }} className="text-red-500 focus:text-red-500">
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden px-4 py-6 sm:px-8 sm:py-8">
          <div className="w-full">
            <Suspense fallback={<div className="py-20 text-center text-sm text-muted-foreground" data-testid="page-loading">Caricamento…</div>}><Outlet /></Suspense>
          </div>
        </main>
      </div>
      <AddToDeviceDialog open={addOpen} onOpenChange={setAddOpen} clientName={clientDisplayName} />
      {/* Promemoria one-shot su mobile alla prima visita (non blocca UI). */}
      <BookmarkAppPrompt clientName={clientDisplayName} />
    </div>
  );
}
